import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { EASE, WeaveWordmark, scrollToId } from "./Weave";

const LINKS = [
  { label: "The shift", id: "shift" },
  { label: "The programme", id: "programme" },
  { label: "Why us", id: "why" },
  { label: "The room", id: "cohort" },
  { label: "Tuition", id: "tuition" },
  { label: "FAQ", id: "faq" },
];

export default function Nav({ onJoin }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 60 : 0);
  };

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-paper/90 shadow-[0_1px_0_0_#E4DACB] backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
        <button
          data-testid="nav-logo-home"
          onClick={() => window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.2 }) : window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2.5"
          aria-label="The Weave — back to top"
        >
          <WeaveWordmark />
          <span className="ml-2 hidden rounded-full border border-paperborder px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-body2 md:inline-block">
            Founder Academy
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-body2 transition-colors hover:text-wred"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta-join-waitlist"
            onClick={onJoin}
            className="group relative overflow-hidden rounded-full bg-ink px-6 py-3 text-sm font-semibold text-paper transition-colors hover:bg-wred"
          >
            Join the waitlist
          </button>
        </nav>

        <div className="flex items-center gap-3 lg:hidden">
          <button
            data-testid="nav-cta-join-waitlist-mobile"
            onClick={onJoin}
            className="rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-paper transition-colors hover:bg-wred"
          >
            Join the waitlist
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-paperborder bg-paper/80 text-ink"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden border-t border-paperborder bg-paper/95 backdrop-blur-md lg:hidden"
            aria-label="Mobile"
          >
            <div className="space-y-1 px-6 py-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-link-mobile-${l.id}`}
                  onClick={() => go(l.id)}
                  className="block w-full rounded-lg px-3 py-3 text-left text-lg font-medium text-ink transition-colors hover:bg-papermuted"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
